import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Container, Grid, Card, CardContent, CardMedia, Typography, Button, TextField, Dialog, DialogActions, DialogContent, DialogTitle, Box, FormControl, InputLabel, Select, MenuItem, InputAdornment, Pagination } from '@mui/material';
import { styled } from '@mui/system';
import { ToastContainer, toast } from 'react-toastify';
import SearchIcon from '@mui/icons-material/Search';
import 'react-toastify/dist/ReactToastify.css';

const ResizableTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-root': {
    resize: 'vertical',
    overflow: 'auto',
  },
  '& .MuiInputBase-input': {
    width: '100%',
  },
}));

const AdminAds = () => {
  const [ads, setAds] = useState([]);
  const [open, setOpen] = useState(false);
  const [currentAd, setCurrentAd] = useState({
    id: 0,
    title: '',
    adCopy: '',
    niche: '',
    type: '',
    publisher: '',
    publicationDate: '',
    imageUrl: '',
    copyCount: 0,
    userRating: 0,
    educationalContent: ''
  });
  const [errors, setErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [adSize, setAdSize] = useState('');
  const [sortOrder, setSortOrder] = useState('');
  const [page, setPage] = useState(1);

  // Ref for dynamically adjusting the height of the text area
  const textFieldRef = useRef(null);

  const fetchAds = async () => {
    try {
      const response = await axios.get('https://api.typecopy.co.za/api/Ads');
      setAds(response.data);
    } catch (error) {
      console.error('Error fetching ads:', error);
    }
  };

  useEffect(() => {
    fetchAds();
  }, []);

  const handleOpen = (ad) => {
    setCurrentAd(ad);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setCurrentAd({
      id: 0,
      title: '',
      adCopy: '',
      niche: '',
      type: '',
      publisher: '',
      publicationDate: '',
      imageUrl: '',
      copyCount: 0,
      userRating: 0,
      educationalContent: ''
    });
    setErrors({});
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCurrentAd({ ...currentAd, [name]: value });
  };

  const validate = () => {
    const newErrors = {};
    if (!currentAd.title) newErrors.title = 'Title is required';
    if (!currentAd.adCopy) newErrors.adCopy = 'Ad Copy is required';
    if (!currentAd.niche) newErrors.niche = 'Niche is required';
    if (!currentAd.type) newErrors.type = 'Type is required';
    if (!currentAd.publisher) newErrors.publisher = 'Publisher is required';
    if (!currentAd.publicationDate) newErrors.publicationDate = 'Publication Date is required';
    if (!currentAd.imageUrl) newErrors.imageUrl = 'Image URL is required';
    else if (!/^https?:\/\/.+\..+/.test(currentAd.imageUrl)) newErrors.imageUrl = 'Invalid URL format';
    if (currentAd.copyCount < 0) newErrors.copyCount = 'Copy Count must be at least 0';
    if (currentAd.userRating < 0 || currentAd.userRating > 5) newErrors.userRating = 'User Rating must be between 0 and 5';
    if (!currentAd.educationalContent) newErrors.educationalContent = 'Educational Content is required';
    return newErrors;
  };

  const handleSave = async () => {
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    try {
      if (currentAd.id) {
        await axios.put(`https://api.typecopy.co.za/api/Ads/${currentAd.id}`, currentAd);
      } else {
        await axios.post('https://api.typecopy.co.za/api/Ads', currentAd);
      }
      fetchAds();
      handleClose();
    } catch (error) {
      console.error('Error saving ad:', error);
      toast.error('There was an error saving the ad');
    }
  };

  const handleDelete = async (id) => {
    await axios.delete(`https://api.typecopy.co.za/api/Ads/${id}`);
    fetchAds();
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleAdSizeChange = (event) => {
    setAdSize(event.target.value);
  };

  const handleSortOrderChange = (event) => {
    setSortOrder(event.target.value);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const getAdSize = (copyCount) => {
    if (copyCount < 100) {
      return 'small';
    } else if (copyCount < 500) {
      return 'medium';
    } else {
      return 'large';
    }
  };

  const filteredAds = ads
    .filter((ad) =>
      ad.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ad.niche.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .filter((ad) => (adSize ? getAdSize(ad.copyCount) === adSize : true))
    .sort((a, b) => {
      if (sortOrder === 'asc') {
        return a.title.localeCompare(b.title);
      } else if (sortOrder === 'desc') {
        return b.title.localeCompare(a.title);
      }
      return 0;
    });

  const adsPerPage = 9;
  const paginatedAds = filteredAds.slice((page - 1) * adsPerPage, page * adsPerPage);
  const pageCount = Math.ceil(filteredAds.length / adsPerPage);

  // Resize text area based on content
  const handleResize = () => {
    const input = textFieldRef.current;
    if (input) {
      input.style.height = 'auto'; // Reset height
      input.style.height = `${input.scrollHeight}px`; // Adjust height based on content
    }
  };

  useEffect(() => {
    handleResize(); // Adjust text field height when content changes
  }, [currentAd.adCopy]);

  return (
    <Container>
      <Typography variant="h4" gutterBottom>
        Admin Ads
      </Typography>
      <Box mb={0.5}>
        <Button variant="contained" color="primary" onClick={() => handleOpen({
          id: 0,
          title: '',
          adCopy: '',
          niche: '',
          type: '',
          publisher: '',
          publicationDate: '',
          imageUrl: '',
          copyCount: 0,
          userRating: 0,
          educationalContent: ''
        })}>
          Add New Ad
        </Button>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
          <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Ad Size</InputLabel>
            <Select
              value={adSize}
              onChange={handleAdSizeChange}
              label="Ad Size"
            >
              <MenuItem value=""><em>None</em></MenuItem>
              <MenuItem value="small">Small</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="large">Large</MenuItem>
            </Select>
          </FormControl>
          <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Sort Order</InputLabel>
            <Select
              value={sortOrder}
              onChange={handleSortOrderChange}
              label="Sort Order"
            >
              <MenuItem value=""><em>None</em></MenuItem>
              <MenuItem value="asc">Alphabetically (A-Z)</MenuItem>
              <MenuItem value="desc">Alphabetically (Z-A)</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search Ads"
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 300 }}
          />
        </Box>
      </Box>
      <Grid container spacing={4}>
        {paginatedAds.map((ad) => (
          <Grid item key={ad.id} xs={12} sm={6} md={4}>
            <Card>
              <CardMedia
                component="img"
                height="140"
                image={ad.imageUrl}
                alt={ad.title}
              />
              <CardContent>
                <Typography variant="h6">{ad.title}</Typography>
                <Typography variant="body2" color="textSecondary">{ad.niche}</Typography>
                <Typography variant="body2" color="textSecondary">{ad.publisher}</Typography>
                <Typography variant="body2" color="textSecondary">Rating: {ad.userRating}</Typography>
                <Typography variant="body2" color="textSecondary">Size: {ad.copyCount}</Typography>
                <Button onClick={() => handleOpen(ad)} color="primary">Edit</Button>
                <Button onClick={() => handleDelete(ad.id)} color="secondary">Delete</Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Pagination
        count={pageCount}
        page={page}
        onChange={handlePageChange}
        color="primary"
        sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}
      />

      {/* Dialog for editing ad */}
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{currentAd.id ? 'Edit Ad' : 'Add New Ad'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Title"
            name="title"
            value={currentAd.title}
            onChange={handleChange}
            fullWidth
            error={Boolean(errors.title)}
            helperText={errors.title}
            margin="normal"
          />
          <ResizableTextField
            label="Ad Copy"
            name="adCopy"
            value={currentAd.adCopy}
            onChange={handleChange}
            fullWidth
            multiline
            minRows={4}
            error={Boolean(errors.adCopy)}
            helperText={errors.adCopy}
            inputRef={textFieldRef}
            onInput={handleResize}
            margin="normal"
          />
          <TextField
            label="Niche"
            name="niche"
            value={currentAd.niche}
            onChange={handleChange}
            fullWidth
            error={Boolean(errors.niche)}
            helperText={errors.niche}
            margin="normal"
          />
          <TextField
            label="Type"
            name="type"
            value={currentAd.type}
            onChange={handleChange}
            fullWidth
            error={Boolean(errors.type)}
            helperText={errors.type}
            margin="normal"
          />
          <TextField
            label="Publisher"
            name="publisher"
            value={currentAd.publisher}
            onChange={handleChange}
            fullWidth
            error={Boolean(errors.publisher)}
            helperText={errors.publisher}
            margin="normal"
          />
          <TextField
            label="Publication Date"
            name="publicationDate"
            value={currentAd.publicationDate}
            onChange={handleChange}
            fullWidth
            type="date"
            error={Boolean(errors.publicationDate)}
            helperText={errors.publicationDate}
            InputLabelProps={{
              shrink: true,
            }}
            margin="normal"
          />
          <TextField
            label="Image URL"
            name="imageUrl"
            value={currentAd.imageUrl}
            onChange={handleChange}
            fullWidth
            error={Boolean(errors.imageUrl)}
            helperText={errors.imageUrl}
            margin="normal"
          />
          <TextField
            label="Copy Count"
            name="copyCount"
            value={currentAd.copyCount}
            onChange={handleChange}
            fullWidth
            type="number"
            error={Boolean(errors.copyCount)}
            helperText={errors.copyCount}
            margin="normal"
          />
          <TextField
            label="User Rating"
            name="userRating"
            value={currentAd.userRating}
            onChange={handleChange}
            fullWidth
            type="number"
            error={Boolean(errors.userRating)}
            helperText={errors.userRating}
            margin="normal"
            InputProps={{
              inputProps: {
                min: 0,
                max: 5
              }
            }}
          />
          <TextField
            label="Educational Content"
            name="educationalContent"
            value={currentAd.educationalContent}
            onChange={handleChange}
            fullWidth
            error={Boolean(errors.educationalContent)}
            helperText={errors.educationalContent}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSave} color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <ToastContainer />
    </Container>
  );
};

export default AdminAds;
